import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { ToastContext } from './ToastContext';
import type { ToastAction, ToastOptions } from './ToastContext';
import { IconButton } from '@/ui/IconButton';

interface ToastItem {
  id: number;
  msg: string;
  action?: ToastAction;
  options?: ToastOptions;
}

const Viewport = styled.div`
  position: fixed;
  z-index: 200;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
`;

const Item = styled.div`
  pointer-events: auto;
  background: ${({ theme }) => theme.color.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.elevation.md};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Msg = styled.span`
  flex: 1;
`;

const ActionBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${({ theme }) => theme.color.accentCyan};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

function ToastMessage({ item, dismiss }: { item: ToastItem; dismiss: (id: number) => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remaining = useRef(item.options?.durationMs ?? 4000);
  const paused = item.options?.pauseOnInteraction && (hovered || focused);
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => dismiss(item.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [dismiss, item.id, paused]);

  return (
    <Item
      role="status"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <Msg>{item.msg}</Msg>
      {item.action && (
        <ActionBtn
          type="button"
          onClick={() => {
            item.action?.onClick();
            dismiss(item.id);
          }}
        >
          {item.action.label}
        </ActionBtn>
      )}
      <IconButton icon="close" label="Dismiss" size={14} onClick={() => dismiss(item.id)} />
    </Item>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const dismissCallbacks = useRef(new Map<number, () => void>());

  const dismiss = useCallback((id: number) => {
    const callback = dismissCallbacks.current.get(id);
    dismissCallbacks.current.delete(id);
    callback?.();
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (msg: string, action?: ToastAction, options?: ToastOptions) => {
      const id = nextId.current++;
      if (options?.onDismiss) dismissCallbacks.current.set(id, options.onDismiss);
      setItems((prev) => [...prev, { id, msg, action, options }]);
      return () => dismiss(id);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Omit the viewport entirely while empty — an always-mounted (if visually
          empty) node broke callers asserting a container is fully empty, and
          there's nothing for aria-live to usefully announce anyway. */}
      {items.length > 0 && (
        <Viewport aria-live="polite">
          {items.map((t) => (
            <ToastMessage key={t.id} item={t} dismiss={dismiss} />
          ))}
        </Viewport>
      )}
    </ToastContext.Provider>
  );
}
